import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { appAnalyticsAPI } from '../../services/api';
import { AppNav, AppStyles, CodeBlock, copyText, fmtAgo } from './common';
import { PLATFORM_LABELS, Platform, initSnippet, installSnippet, trackSnippet } from './snippets';

type Answers = {
  category: string; platforms: string[]; has_splash: boolean; has_auth: boolean; auth_methods: string[];
  guest_mode: boolean; onboarding: boolean; has_cart: boolean; subscriptions: boolean; push_notifications: boolean;
  key_action: string; custom_screens: string[];
};

const DEFAULT_ANSWERS: Answers = {
  category: 'ecommerce', platforms: ['react-native'], has_splash: true, has_auth: true, auth_methods: ['email'],
  guest_mode: false, onboarding: false, has_cart: true, subscriptions: false, push_notifications: false, key_action: '', custom_screens: [],
};

const CATEGORY_ICONS: Record<string, string> = {
  ecommerce: 'bi-bag', food_delivery: 'bi-basket', booking_travel: 'bi-airplane', fintech: 'bi-bank', content_media: 'bi-play-btn',
  education: 'bi-mortarboard', healthcare: 'bi-heart-pulse', saas: 'bi-kanban', other: 'bi-grid',
};

const CAT_BADGE: Record<string, string> = {
  lifecycle: 'secondary', onboarding: 'info', auth: 'primary', engagement: 'dark', commerce: 'warning', conversion: 'success', custom: 'light',
};

const Toggle: React.FC<{ label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void; id: string }> = ({ label, hint, checked, onChange, id }) => (
  <div className="form-check form-switch mb-2">
    <input className="form-check-input" type="checkbox" role="switch" id={id} checked={checked} onChange={e => onChange(e.target.checked)} />
    <label className="form-check-label" htmlFor={id}>{label}{hint && <span className="text-muted small d-block">{hint}</span>}</label>
  </div>
);

const AppSetupPage: React.FC = () => {
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;
  const [meta, setMeta] = useState<any>({ categories: [], platforms: [], funnels: {} });
  const [loading, setLoading] = useState(true);
  const [flow, setFlow] = useState<any>(null);
  const [rnInstallUrl, setRnInstallUrl] = useState('');
  const [stage, setStage] = useState<1 | 2 | 3>(1);
  const [answers, setAnswers] = useState<Answers>(DEFAULT_ANSWERS);
  const [steps, setSteps] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [screenInput, setScreenInput] = useState('');
  const [platform, setPlatform] = useState<Platform>('react-native');
  const [verification, setVerification] = useState<any>(null);
  const [showKey, setShowKey] = useState(false);
  const pollRef = useRef<any>(null);
  const origin = appAnalyticsAPI.ingestOrigin();

  const load = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const [m, f] = await Promise.all([appAnalyticsAPI.getMeta(), appAnalyticsAPI.getFlow(assetId)]);
      appAnalyticsAPI.getSdkLinks(assetId).then((r) => setRnInstallUrl(r.data?.data?.react_native || '')).catch(() => {});
      setMeta(m.data?.data || meta);
      const fl = f.data?.data?.flow;
      setFlow(fl);
      if (fl) {
        setAnswers({ ...DEFAULT_ANSWERS, ...(fl.answers || {}) });
        setSteps(fl.steps);
        setStage(3);
        setPlatform(((fl.answers?.platforms || [])[0] as Platform) || 'react-native');
      } else {
        setStage(1);
        setFlow(null);
        setSteps([]);
        setAnswers(DEFAULT_ANSWERS);
      }
    } catch {
      toast.error('Could not load the app setup');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  useEffect(() => { load(); }, [load]);

  // Live verification polling on the integrate step
  useEffect(() => {
    if (stage !== 3 || !flow?.id) return;
    const tick = () => appAnalyticsAPI.getVerification(flow.id).then(r => setVerification(r.data?.data)).catch(() => {});
    tick();
    pollRef.current = setInterval(tick, 5000);
    return () => clearInterval(pollRef.current);
  }, [stage, flow?.id]);

  const set = <K extends keyof Answers>(k: K, v: Answers[K]) => setAnswers(a => ({ ...a, [k]: v }));
  const toggleIn = (k: 'platforms' | 'auth_methods', v: string) =>
    setAnswers(a => ({ ...a, [k]: a[k].includes(v) ? a[k].filter(x => x !== v) : [...a[k], v] }));

  const generate = async () => {
    if (!answers.platforms.length) { toast.error('Pick at least one platform'); return; }
    try {
      const r = await appAnalyticsAPI.previewPlan(answers);
      const generated = r.data?.data?.steps || [];
      // keep user's custom steps when regenerating
      const custom = steps.filter(s => s.is_custom && !generated.some((g: any) => g.step_key === s.step_key));
      setSteps([...generated.slice(0, -1), ...custom, ...generated.slice(-1)]);
      setStage(2);
    } catch {
      toast.error('Could not generate the plan');
    }
  };

  const savePlan = async () => {
    if (!assetId) return;
    setSaving(true);
    try {
      const r = await appAnalyticsAPI.saveFlow(assetId, answers, steps.map((s, i) => ({ ...s, sort_order: i })));
      const fl = r.data?.data?.flow;
      setFlow(fl);
      setSteps(fl.steps);
      setStage(3);
      if (!answers.platforms.includes(platform)) setPlatform((answers.platforms[0] as Platform) || 'react-native');
      toast.success(flow ? 'Tracking plan updated' : 'Tracking plan created — your app key is ready');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not save the plan');
    } finally {
      setSaving(false);
    }
  };

  const move = (i: number, d: -1 | 1) => setSteps(s => {
    const j = i + d;
    if (j < 0 || j >= s.length) return s;
    const c = [...s];
    [c[i], c[j]] = [c[j], c[i]];
    return c;
  });
  const updateStep = (i: number, patch: any) => setSteps(s => s.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const removeStep = (i: number) => setSteps(s => s.filter((_, k) => k !== i));
  const addCustomStep = () => {
    const taken = new Set(steps.map(s => s.step_key));
    let n = 1;
    while (taken.has(`custom_step_${n}`)) n++;
    const s = { step_key: `custom_step_${n}`, display_name: `Custom step ${n}`, description: '', when_to_call: '', category: 'custom', properties: [], funnels: [], is_conversion: false, is_auto: false, is_custom: true };
    setSteps(x => [...x.slice(0, -1), s, ...x.slice(-1)]);
    setExpanded(s.step_key);
  };
  const toggleFunnel = (i: number, f: string) => {
    const cur: string[] = steps[i].funnels || [];
    updateStep(i, { funnels: cur.includes(f) ? cur.filter(x => x !== f) : [...cur, f] });
  };

  const saveSettings = async (patch: any) => {
    if (!flow) return;
    try {
      const r = await appAnalyticsAPI.updateSettings(flow.id, patch);
      setFlow(r.data?.data?.flow);
      toast.success('Settings saved');
    } catch { toast.error('Could not save settings'); }
  };

  const rotate = async (revoke: boolean) => {
    if (!flow) return;
    const msg = revoke
      ? 'Rotate the key and REVOKE the old one immediately? Apps still using it will stop sending data.'
      : 'Rotate the app key? The old key keeps working for 7 days so you can ship an update.';
    if (!window.confirm(msg)) return;
    try {
      const r = await appAnalyticsAPI.rotateKey(flow.id, revoke);
      setFlow(r.data?.data?.flow);
      toast.success('New app key generated');
    } catch { toast.error('Could not rotate the key'); }
  };

  const verifyByKey = useMemo(() => new Map((verification?.steps || []).map((s: any) => [s.step_key, s])), [verification]);
  const platformsShown: Platform[] = useMemo(() => {
    const p = (flow?.answers?.platforms || answers.platforms || []).filter((x: string) => x in PLATFORM_LABELS) as Platform[];
    return [...(p.length ? p : ['react-native' as Platform]), 'rest'];
  }, [flow, answers.platforms]);
  const appKey = flow?.app_key || 'cak_your_app_key';

  const StepHeader = (
    <div className="d-flex gap-2 mb-3 flex-wrap">
      {[{ n: 1, t: 'App flow questions' }, { n: 2, t: 'Tracking plan' }, { n: 3, t: 'Integrate & verify' }].map(s => (
        <button key={s.n} type="button" disabled={(s.n === 3 && !flow) || (s.n === 2 && !steps.length)}
          className={`btn btn-sm ${stage === s.n ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setStage(s.n as any)}>
          <span className="badge rounded-pill bg-white text-dark me-1">{s.n}</span>{s.t}
        </button>
      ))}
    </div>
  );

  return (
    <DashboardLayout>
      <AppStyles />
      <div className="container-fluid py-4">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-2">
          <div>
            <h4 className="fw-bold mb-0"><i className="bi bi-phone text-primary me-2"></i>Mobile app integration</h4>
            <p className="text-muted small mb-0">Describe your app flow → get a tracking plan, SDK code for each step, and live verification.</p>
          </div>
          {verification && (
            <span className={`badge ${verification.connected ? 'bg-success' : 'bg-secondary'} align-self-center`}>
              <i className={`bi ${verification.connected ? 'bi-wifi' : 'bi-wifi-off'} me-1`}></i>
              {verification.connected ? `SDK connected · last event ${fmtAgo(verification.last_event_at)}` : 'Waiting for the first event'}
            </span>
          )}
        </div>
        <AppNav />

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
        ) : (
          <>
            {StepHeader}

            {/* ── 1. Questions ── */}
            {stage === 1 && (
              <div className="row g-3">
                <div className="col-lg-8">
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <h6 className="fw-bold mb-3">What kind of app is it?</h6>
                      <div className="row g-2">
                        {meta.categories.map((c: any) => (
                          <div className="col-6 col-md-4" key={c.key}>
                            <button type="button" onClick={() => set('category', c.key)}
                              className={`btn w-100 text-start ${answers.category === c.key ? 'btn-primary' : 'btn-outline-secondary'}`}>
                              <i className={`bi ${CATEGORY_ICONS[c.key] || 'bi-grid'} me-2`}></i>{c.label}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <h6 className="fw-bold mb-3">Platforms & frameworks</h6>
                      <div className="d-flex flex-wrap gap-2">
                        {(['react-native', 'android', 'ios', 'flutter'] as Platform[]).map(p => (
                          <button key={p} type="button" onClick={() => toggleIn('platforms', p)}
                            className={`btn btn-sm ${answers.platforms.includes(p) ? 'btn-primary' : 'btn-outline-secondary'}`}>
                            <i className={`bi ${PLATFORM_LABELS[p].icon} me-1`}></i>{PLATFORM_LABELS[p].label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <h6 className="fw-bold mb-3">Your app's flow</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <Toggle id="q-splash" label="Splash / launch screen" checked={answers.has_splash} onChange={v => set('has_splash', v)} />
                          <Toggle id="q-onb" label="Onboarding / tutorial screens" checked={answers.onboarding} onChange={v => set('onboarding', v)} />
                          <Toggle id="q-auth" label="Login / registration" checked={answers.has_auth} onChange={v => set('has_auth', v)} />
                          {answers.has_auth && (
                            <div className="ms-4 mb-2 d-flex flex-wrap gap-2">
                              {['email', 'phone', 'otp', 'social'].map(m => (
                                <div className="form-check form-check-inline m-0" key={m}>
                                  <input className="form-check-input" type="checkbox" id={`am-${m}`} checked={answers.auth_methods.includes(m)} onChange={() => toggleIn('auth_methods', m)} />
                                  <label className="form-check-label small" htmlFor={`am-${m}`}>{m === 'otp' ? 'OTP' : m[0].toUpperCase() + m.slice(1)}</label>
                                </div>
                              ))}
                            </div>
                          )}
                          <Toggle id="q-guest" label="Guest mode (use without an account)" checked={answers.guest_mode} onChange={v => set('guest_mode', v)} />
                        </div>
                        <div className="col-md-6">
                          <Toggle id="q-cart" label="Cart, checkout & payment" checked={answers.has_cart} onChange={v => set('has_cart', v)} />
                          <Toggle id="q-subs" label="Subscriptions / in-app purchases" checked={answers.subscriptions} onChange={v => set('subscriptions', v)} />
                          <Toggle id="q-push" label="Push notifications" checked={answers.push_notifications} onChange={v => set('push_notifications', v)} />
                        </div>
                      </div>
                      <div className="row g-2 mt-1">
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">The "aha" action of your app (optional)</label>
                          <input className="form-control form-control-sm" placeholder="e.g. created first project" value={answers.key_action} onChange={e => set('key_action', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">Key custom screens to track (optional)</label>
                          <div className="input-group input-group-sm">
                            <input className="form-control" placeholder="e.g. Wallet" value={screenInput} onChange={e => setScreenInput(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter' && screenInput.trim()) { e.preventDefault(); set('custom_screens', [...answers.custom_screens, screenInput.trim()]); setScreenInput(''); } }} />
                            <button type="button" className="btn btn-outline-secondary" onClick={() => { if (screenInput.trim()) { set('custom_screens', [...answers.custom_screens, screenInput.trim()]); setScreenInput(''); } }}>Add</button>
                          </div>
                          <div className="mt-1 d-flex flex-wrap gap-1">
                            {answers.custom_screens.map((s, i) => (
                              <span key={i} className="badge bg-light text-dark border">{s}
                                <i className="bi bi-x ms-1" role="button" onClick={() => set('custom_screens', answers.custom_screens.filter((_, k) => k !== i))}></i></span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button className="btn btn-primary" onClick={generate}><i className="bi bi-magic me-1"></i>Generate tracking plan</button>
                </div>
                <div className="col-lg-4">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body small">
                      <h6 className="fw-bold"><i className="bi bi-shield-check text-success me-1"></i>Safe by design</h6>
                      <ul className="ps-3 mb-2">
                        <li>The SDK never runs on the UI thread and never throws into your app.</li>
                        <li>If CloudAyn is down, events wait in a bounded on-device queue and are retried later with backoff.</li>
                        <li>The app key is write-only — safe to ship inside your app.</li>
                        <li>No advertising IDs, device IDs or IP addresses are collected.</li>
                      </ul>
                      <Link to="/docs/app-integration">Read the integration guide →</Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── 2. Plan editor ── */}
            {stage === 2 && (
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    <div>
                      <h6 className="fw-bold mb-0">Lifecycle & tracking plan ({steps.length} steps)</h6>
                      <div className="text-muted small">Ordered as a user moves through your app. Reorder, rename, mark conversions, add your own steps.</div>
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-secondary" onClick={addCustomStep}><i className="bi bi-plus-lg me-1"></i>Custom step</button>
                      <button className="btn btn-sm btn-primary" onClick={savePlan} disabled={saving}>
                        {saving ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-check2 me-1"></i>}Save plan
                      </button>
                    </div>
                  </div>
                  <div className="list-group list-group-flush">
                    {steps.map((s, i) => (
                      <div key={s.step_key + i} className="list-group-item px-1 ca-step-row">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <div className="btn-group-vertical btn-group-sm">
                            <button className="btn btn-link btn-sm p-0 text-muted" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up"><i className="bi bi-chevron-up"></i></button>
                            <button className="btn btn-link btn-sm p-0 text-muted" onClick={() => move(i, 1)} disabled={i === steps.length - 1} aria-label="Move down"><i className="bi bi-chevron-down"></i></button>
                          </div>
                          <span className="badge rounded-pill bg-light text-dark border" style={{ width: 30 }}>{i + 1}</span>
                          <div className="flex-grow-1" style={{ minWidth: 220 }}>
                            <input className="form-control form-control-sm fw-semibold border-0 px-1" value={s.display_name} onChange={e => updateStep(i, { display_name: e.target.value })} />
                            <code className="small ms-1">{s.step_key}</code>
                            {s.is_auto && <span className="badge bg-secondary-subtle text-secondary ms-2">auto-collected</span>}
                            <span className={`badge bg-${CAT_BADGE[s.category] || 'light'} ms-1 ${s.category === 'custom' ? 'text-dark border' : ''}`}>{s.category}</span>
                          </div>
                          <div className="form-check form-switch m-0" title="Counts as a conversion">
                            <input className="form-check-input" type="checkbox" id={`conv-${i}`} checked={!!s.is_conversion} onChange={e => updateStep(i, { is_conversion: e.target.checked })} />
                            <label className="form-check-label small" htmlFor={`conv-${i}`}>Conversion</label>
                          </div>
                          <div className="form-check m-0" title="Part of the main lifecycle funnel">
                            <input className="form-check-input" type="checkbox" id={`lf-${i}`} checked={(s.funnels || []).includes('lifecycle')} onChange={() => toggleFunnel(i, 'lifecycle')} />
                            <label className="form-check-label small" htmlFor={`lf-${i}`}>In lifecycle funnel</label>
                          </div>
                          <button className="btn btn-sm btn-link" onClick={() => setExpanded(expanded === s.step_key ? null : s.step_key)}>
                            {expanded === s.step_key ? 'Hide' : 'Details'}
                          </button>
                          <button className="btn btn-sm btn-link text-danger" onClick={() => removeStep(i)} aria-label="Remove step"><i className="bi bi-trash"></i></button>
                        </div>
                        {expanded === s.step_key && (
                          <div className="row g-2 mt-1 ms-4">
                            {s.is_custom && (
                              <div className="col-md-4">
                                <label className="form-label small">Event key (what the app sends)</label>
                                <input className="form-control form-control-sm font-monospace" value={s.step_key}
                                  onChange={e => { const v = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'); setExpanded(v); updateStep(i, { step_key: v }); }} />
                              </div>
                            )}
                            <div className="col-md-4">
                              <label className="form-label small">Description</label>
                              <textarea className="form-control form-control-sm" rows={2} value={s.description || ''} onChange={e => updateStep(i, { description: e.target.value })} />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label small">When to call it</label>
                              <textarea className="form-control form-control-sm" rows={2} value={s.when_to_call || ''} onChange={e => updateStep(i, { when_to_call: e.target.value })} />
                            </div>
                            <div className="col-12">
                              <label className="form-label small mb-1">Properties</label>
                              <table className="table table-sm small mb-1">
                                <thead><tr><th>Name</th><th>Type</th><th>Required</th><th>Description</th><th></th></tr></thead>
                                <tbody>
                                  {(s.properties || []).map((p: any, k: number) => {
                                    const setP = (patch: any) => updateStep(i, { properties: s.properties.map((x: any, m: number) => (m === k ? { ...x, ...patch } : x)) });
                                    return (
                                      <tr key={k}>
                                        <td><input className="form-control form-control-sm font-monospace" value={p.name} onChange={e => setP({ name: e.target.value })} /></td>
                                        <td>
                                          <select className="form-select form-select-sm" value={p.type} onChange={e => setP({ type: e.target.value })}>
                                            {['string', 'number', 'boolean', 'object'].map(t => <option key={t}>{t}</option>)}
                                          </select>
                                        </td>
                                        <td><input type="checkbox" className="form-check-input" checked={!!p.required} onChange={e => setP({ required: e.target.checked })} /></td>
                                        <td><input className="form-control form-control-sm" value={p.description || ''} onChange={e => setP({ description: e.target.value })} /></td>
                                        <td><button className="btn btn-sm btn-link text-danger p-0" onClick={() => updateStep(i, { properties: s.properties.filter((_: any, m: number) => m !== k) })}><i className="bi bi-x-lg"></i></button></td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => updateStep(i, { properties: [...(s.properties || []), { name: 'property', type: 'string', required: false }] })}>
                                <i className="bi bi-plus"></i> Property
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── 3. Integrate & verify ── */}
            {stage === 3 && flow && (
              <div className="row g-3">
                <div className="col-xl-8">
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          <div className="small text-muted">App key (public, write-only — safe to embed in your app)</div>
                          <code className="fs-6">{showKey ? flow.app_key : flow.app_key.slice(0, 10) + '•'.repeat(24) + flow.app_key.slice(-6)}</code>
                          {flow.previous_key_expires_at && <div className="small text-warning">Previous key still accepted until {new Date(flow.previous_key_expires_at).toLocaleString()}</div>}
                        </div>
                        <div className="d-flex gap-2">
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowKey(!showKey)}><i className={`bi ${showKey ? 'bi-eye-slash' : 'bi-eye'}`}></i></button>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => copyText(flow.app_key)}><i className="bi bi-clipboard me-1"></i>Copy</button>
                          <div className="dropdown">
                            <button className="btn btn-sm btn-outline-danger dropdown-toggle" data-bs-toggle="dropdown">Rotate</button>
                            <ul className="dropdown-menu dropdown-menu-end small">
                              <li><button className="dropdown-item" onClick={() => rotate(false)}>Rotate (old key valid 7 days)</button></li>
                              <li><button className="dropdown-item text-danger" onClick={() => rotate(true)}>Rotate & revoke old key now</button></li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-header bg-white border-0 pb-0">
                      <ul className="nav nav-tabs card-header-tabs">
                        {platformsShown.map(p => (
                          <li className="nav-item" key={p}>
                            <button className={`nav-link ${platform === p ? 'active' : ''}`} onClick={() => setPlatform(p)}>
                              <i className={`bi ${PLATFORM_LABELS[p].icon} me-1`}></i>{PLATFORM_LABELS[p].label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="card-body">
                      <h6 className="fw-bold">1. Install</h6>
                      <CodeBlock code={installSnippet(platform, rnInstallUrl)} title={PLATFORM_LABELS[platform].lang} />
                      <h6 className="fw-bold mt-3">2. Initialise once at startup</h6>
                      <CodeBlock code={initSnippet(platform, appKey, origin)} title={PLATFORM_LABELS[platform].lang} />
                      <p className="small text-muted">
                        <i className="bi bi-info-circle me-1"></i>
                        {platform === 'rest'
                          ? 'Send up to 100 events per request. Each event needs a unique message_id (used for de-duplication) and a stable anonymous_id per install.'
                          : 'App install (first_open), app open, app background and sessions are tracked automatically. Add the calls below at the matching moments in your app.'}
                      </p>
                      <h6 className="fw-bold mt-3">3. Call each lifecycle step</h6>
                      {steps.filter(s => !s.is_auto || platform === 'rest').map(s => {
                        const v: any = verifyByKey.get(s.step_key);
                        return (
                          <div key={s.step_key} className="border rounded p-2 mb-2">
                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-1">
                              <div>
                                <span className="fw-semibold">{s.display_name}</span> <code className="small">{s.step_key}</code>
                                {s.is_conversion && <span className="badge bg-success-subtle text-success ms-1">conversion</span>}
                                <div className="small text-muted"><i className="bi bi-clock me-1"></i>{s.when_to_call}</div>
                                {(s.properties || []).some((p: any) => p.required) && (
                                  <div className="small">Required: {s.properties.filter((p: any) => p.required).map((p: any) => <code key={p.name} className="me-1">{p.name}</code>)}</div>
                                )}
                              </div>
                              {v?.verified
                                ? <span className="badge bg-success"><i className="bi bi-check-circle me-1"></i>Received · {v.total_events} · {fmtAgo(v.last_seen_at)}</span>
                                : <span className="badge bg-light text-muted border"><span className="spinner-grow spinner-grow-sm me-1" style={{ width: 8, height: 8 }}></span>Waiting for first event</span>}
                            </div>
                            <CodeBlock small code={trackSnippet(s, platform, appKey, origin)} title={PLATFORM_LABELS[platform].lang} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="col-xl-4">
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <h6 className="fw-bold mb-2"><i className="bi bi-broadcast me-1"></i>Live verification</h6>
                      {verification ? (
                        <>
                          <div className="d-flex justify-content-between small mb-1"><span>Steps verified</span><span className="fw-bold">{verification.verified_steps}/{verification.total_steps}</span></div>
                          <div className="progress mb-2" style={{ height: 8 }}>
                            <div className="progress-bar bg-success" style={{ width: `${verification.total_steps ? (verification.verified_steps / verification.total_steps) * 100 : 0}%` }}></div>
                          </div>
                          <ul className="list-unstyled small mb-2">
                            {verification.steps.map((s: any) => (
                              <li key={s.step_key} className="d-flex align-items-center gap-2 py-1">
                                <i className={`bi ${s.verified ? 'bi-check-circle-fill text-success' : 'bi-circle text-muted'}`}></i>
                                <span className={s.verified ? '' : 'text-muted'}>{s.display_name}</span>
                                {s.verified && <span className="ms-auto text-muted">{s.events_24h} today</span>}
                              </li>
                            ))}
                          </ul>
                          {verification.unplanned_events_24h?.length > 0 && (
                            <div className="alert alert-warning small py-2 mb-2">
                              Events not in your plan (24h): {verification.unplanned_events_24h.map((e: any) => <code key={e.event_name} className="me-1">{e.event_name}</code>)}
                            </div>
                          )}
                          <div className="small fw-semibold mb-1">Latest events</div>
                          <div style={{ maxHeight: 220, overflow: 'auto' }}>
                            {(verification.recent_events || []).length === 0 && <div className="text-muted small">Nothing yet — run your app with the SDK initialised.</div>}
                            {(verification.recent_events || []).map((e: any, k: number) => (
                              <div key={k} className="small d-flex gap-2 border-bottom py-1">
                                <code>{e.event_name === 'screen_view' ? `screen: ${e.screen}` : e.event_name}</code>
                                <span className="text-muted ms-auto">{e.platform || ''} {e.app_version || ''} · {fmtAgo(e.received_at)}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : <div className="text-muted small">Checking…</div>}
                    </div>
                  </div>

                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <h6 className="fw-bold mb-2"><i className="bi bi-sliders me-1"></i>Remote SDK settings</h6>
                      <Toggle id="s-enabled" label="Collection enabled" hint="Kill switch: when off, SDKs stop sending within minutes (no app update needed)."
                        checked={!!flow.is_enabled} onChange={v => saveSettings({ is_enabled: v })} />
                      <label className="form-label small mb-0">Sampling ({Math.round(flow.sampling_rate * 100)}% of installs)</label>
                      <input type="range" className="form-range" min={0} max={100} step={5} defaultValue={Math.round(flow.sampling_rate * 100)}
                        onMouseUp={e => saveSettings({ sampling_rate: Number((e.target as HTMLInputElement).value) / 100 })}
                        onTouchEnd={e => saveSettings({ sampling_rate: Number((e.target as HTMLInputElement).value) / 100 })} />
                      <div className="row g-2">
                        <div className="col-6">
                          <label className="form-label small mb-0">Flush every (s)</label>
                          <input type="number" min={1} max={300} className="form-control form-control-sm" defaultValue={flow.flush_interval_sec}
                            onBlur={e => saveSettings({ flush_interval_sec: Number(e.target.value) })} />
                        </div>
                        <div className="col-6">
                          <label className="form-label small mb-0">Rate limit / min</label>
                          <input type="number" min={1} className="form-control form-control-sm" defaultValue={flow.rate_limit_per_min}
                            onBlur={e => saveSettings({ rate_limit_per_min: Number(e.target.value) })} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card border-0 shadow-sm">
                    <div className="card-body small">
                      <div className="d-flex gap-2 flex-wrap">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setStage(1)}><i className="bi bi-pencil me-1"></i>Edit answers</button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setStage(2)}><i className="bi bi-list-ol me-1"></i>Edit plan</button>
                        <Link className="btn btn-sm btn-outline-primary" to="/docs/app-integration"><i className="bi bi-journal-code me-1"></i>Docs</Link>
                        <Link className="btn btn-sm btn-primary" to="/app-analytics/overview"><i className="bi bi-graph-up me-1"></i>Analytics</Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AppSetupPage;
